import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import {
  LayoutDashboard, BookOpen, HelpCircle, Image, BarChart, Bell, Flag, Settings, ArrowLeft, Menu, X, Users,
} from 'lucide-react';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/admin/topics', label: 'Topics', icon: BookOpen },
  { to: '/admin/questions', label: 'Questions', icon: HelpCircle },
  { to: '/admin/image-quiz', label: 'Image Quiz', icon: Image },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/reports', label: 'Reports', icon: Flag, badge: 'reports' },
  { to: '/admin/ads', label: 'Ads', icon: BarChart },
  { to: '/admin/notifications', label: 'Notifications', icon: Bell },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

export function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openReports, setOpenReports] = useState(0);

  useEffect(() => {
    let mounted = true;
    adminAPI.getQuestionReports('open')
      .then(res => { if (mounted) setOpenReports(res.data.open_count || 0); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [location.pathname]);

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const NavList = ({ onClick }) => (
    <nav className="space-y-1">
      {NAV.map(item => (
        <Link
          key={item.to}
          to={item.to}
          onClick={onClick}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
            isActive(item)
              ? 'bg-teal-500/15 text-teal-300 border border-teal-500/30'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/70'
          }`}
        >
          <item.icon className="w-4 h-4" />
          <span className="flex-1">{item.label}</span>
          {item.badge === 'reports' && openReports > 0 && (
            <span className="text-xs bg-rose-500/90 text-white px-2 py-0.5 rounded-full">{openReports}</span>
          )}
        </Link>
      ))}
      <button
        onClick={() => { setOpen(false); navigate('/home'); }}
        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/70 mt-4 border-t border-slate-800 pt-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to App
      </button>
    </nav>
  );

  return (
    <div className="admin-theme min-h-screen bg-[#0a1628]">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-[#0d1f35]/95 backdrop-blur border-b border-slate-800">
        <span className="font-bold text-white">⚙️ Admin</span>
        <button onClick={() => setOpen(!open)} className="text-slate-300 p-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/60" onClick={() => setOpen(false)}>
          <div className="w-64 h-full bg-[#0d1f35] p-4 pt-16" onClick={e => e.stopPropagation()}>
            <NavList onClick={() => setOpen(false)} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-60 flex-col bg-[#0d1f35] border-r border-slate-800 p-4 z-30">
        <div className="flex items-center gap-2 px-2 mb-6 mt-2">
          <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-teal-400/40">
            <img src="/logo.jpeg" alt="SocraQuest" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="font-bold text-white leading-tight">SocraQuest</p>
            <p className="text-[11px] uppercase tracking-wider text-teal-400">Admin Panel</p>
          </div>
        </div>
        <NavList />
      </aside>

      {/* Content */}
      <main className="md:ml-60 p-4 sm:p-6 lg:p-8">
        {children}
      </main>
    </div>
  );
}
